import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  Search,
  SlidersHorizontal,
  ChevronDown,
  X,
  ArrowUpDown,
  Check,
  AlertCircle,
  Building,
  Home as HomeIcon,
  Map,
  Store
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PropertyCard from '../components/PropertyCard';
import { motion, AnimatePresence } from 'framer-motion';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardAnimation = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { 
      type: "spring", 
      damping: 15,
      duration: 0.6
    }
  },
  hover: {
    y: -8,
    scale: 1.03,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    transition: { duration: 0.2 }
  }
};

// Custom Components
const SectionTitle = ({ title, subtitle }) => (
  <motion.div
    className="flex flex-col items-start mb-8"
    variants={fadeInUp}
  >
    <h1 className="text-4xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700">
      {title}
    </h1>
    {subtitle && (
      <p className="text-gray-600">
        {subtitle}
      </p>
    )}
  </motion.div>
);

const LoadingState = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
    <motion.div
      className="flex flex-col items-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
      <p className="mt-4 text-indigo-700 font-medium">Cargando propiedades...</p>
    </motion.div>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 px-4">
    <motion.div
      className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="text-center">
        <div className="bg-red-50 w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">
          No pudimos cargar las propiedades
        </h2>
        <p className="text-gray-600 mb-6">{message}</p>
        <motion.button
          onClick={onRetry}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          Reintentar
        </motion.button>
      </div>
    </motion.div>
  </div>
);

const AllProperties = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { user } = useAuth();
  
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [userData, setUserData] = useState(null);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [propertiesPerPage] = useState(9);

  // Estados para filtros y ordenamiento
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    tipo: '',
    habitaciones: '',
    banos: '',
    precio_min: '',
    precio_max: '',
    operacion: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showSortOptions, setShowSortOptions] = useState(false);

  // Obtener el icono para el tipo de propiedad
  const getPropertyIcon = (type) => {
    switch (type) {
      case 'casa':
        return HomeIcon;
      case 'departamento':
        return Building;
      case 'terreno':
        return Map;
      case 'local':
        return Store;
      default:
        return Building;
    }
  };

  // Determinar tipo de vista
  const getProfileType = () => {
    const path = location.pathname;
    
    if (path.includes('/vendedor/')) return 'vendedor';
    if (path.includes('/inmobiliaria/')) return 'inmobiliaria';
    if (path.includes('/constructora/')) return 'constructora';
    
    // Para las nuevas rutas
    if (path.includes('/usuario/')) return 'usuario';
    if (path.includes('/propiedades/usuario/')) return 'propiedades_usuario';
    if (path.includes('/alquileres/usuario/')) return 'alquileres_usuario';
    
    return null;
  };

  const isOwnProperties = location.pathname === '/allproperties';
  const profileType = getProfileType();

  // Cargar datos
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const baseUrl = 'https://backend-production-1786.up.railway.app/api';

        let propertiesData = [];
        
        if (isOwnProperties) {
          if (!token || !user) {
            navigate('/login');
            return;
          }
          // Obtener propiedades y alquileres del usuario autenticado
          const [propertiesResponse, rentalsResponse] = await Promise.all([
            fetch(`${baseUrl}/propiedades/usuario/${user.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }),
            fetch(`${baseUrl}/alquileres/usuario/${user.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            })
          ]);

          if (!propertiesResponse.ok || !rentalsResponse.ok) {
            console.error('Error en respuestas:', {
              properties: propertiesResponse.status,
              rentals: rentalsResponse.status
            });
            throw new Error('Error al cargar las propiedades');
          }

          const properties = await propertiesResponse.json();
          const rentals = await rentalsResponse.json();
          
          // Combinar propiedades y alquileres
          propertiesData = [...properties, ...rentals];

        } else if (profileType === 'usuario' || profileType === 'propiedades_usuario' || profileType === 'alquileres_usuario') {
          const userId = params.id; // Obtener ID de los parámetros de ruta
          
          // Determinar qué endpoints llamar según el tipo de perfil
          let endpoints = [];
          let userDataPromise = null;
          
          if (profileType === 'usuario' || profileType === 'propiedades_usuario') {
            endpoints.push(fetch(`${baseUrl}/propiedades/usuario/${userId}`, {
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : undefined
              }
            }));
          }
          
          if (profileType === 'usuario' || profileType === 'alquileres_usuario') {
            endpoints.push(fetch(`${baseUrl}/alquileres/usuario/${userId}`, {
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : undefined
              }
            }));
          }
          
          // Obtener también datos del usuario
          userDataPromise = fetch(`${baseUrl}/vendedores/usuario/${userId}`, {
            headers: { 'Content-Type': 'application/json' }
          });
          
          // Ejecutar las peticiones
          const responses = await Promise.all(endpoints);
          
          // Verificar que todas las respuestas sean ok
          if (responses.some(response => !response.ok)) {
            console.error('Error en respuestas de nuevas rutas:', 
              responses.map(r => r.status)
            );
            throw new Error('Error al cargar las propiedades');
          }
          
          // Procesar los resultados
          const results = await Promise.all(responses.map(response => response.json()));
          
          // Combinar todos los resultados
          propertiesData = results.flat();
          
          // Obtener datos del usuario si la promesa existe
          if (userDataPromise) {
            try {
              const userResponse = await userDataPromise;
              if (userResponse.ok) {
                const userData = await userResponse.json();
                setUserData(userData);
              }
            } catch (userError) {
              console.error('Error al obtener datos de usuario:', userError);
            }
          }
        } else if (profileType && params.nombre_usuario) {
          console.log("Parámetros extraídos:", params); // Agrega este log para depuración
          
          // Obtener propiedades y alquileres públicos del vendedor
          const [propertiesResponse, rentalsResponse, userResponse] = await Promise.all([
            fetch(`${baseUrl}/propiedades/vendedor/${params.nombre_usuario}/public`, {
              headers: {
                'Content-Type': 'application/json'
              }
            }),
            fetch(`${baseUrl}/alquileres/vendedor/${params.nombre_usuario}/public`, {
              headers: {
                'Content-Type': 'application/json'
              }
            }),
            fetch(`${baseUrl}/vendedores/perfil/${params.nombre_usuario}`, {
              headers: {
                'Content-Type': 'application/json'
              }
            })
          ]);

          if (!propertiesResponse.ok || !rentalsResponse.ok) {
            console.error('Error en respuestas de perfil:', {
              properties: propertiesResponse.status,
              rentals: rentalsResponse.status
            });
            throw new Error('Error al cargar las propiedades');
          }

          const properties = await propertiesResponse.json();
          const rentals = await rentalsResponse.json();
          
          // Combinar propiedades y alquileres
          propertiesData = [...properties, ...rentals];

          if (userResponse.ok) {
            const userData = await userResponse.json();
            setUserData(userData);
          }
        } else {
          throw new Error('URL no válida');
        }

        // Ordenar por fecha de publicación
        propertiesData.sort((a, b) => 
          new Date(b.fecha_publicacion) - new Date(a.fecha_publicacion)
        );

        console.log(`Total de propiedades cargadas: ${propertiesData.length}`);
        setProperties(propertiesData);
        setFilteredProperties(propertiesData);

      } catch (err) {
        console.error('Error al cargar los datos:', err);
        setError(err.message || 'Error al cargar las propiedades');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, isOwnProperties, profileType, params.nombre_usuario, params.id, user]);

  // Aplicar filtros y búsqueda
  useEffect(() => {
    let filtered = [...properties];

    if (searchTerm) {
      filtered = filtered.filter(property => 
        property.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.descripcion_corta?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Aplicar filtros considerando ambos tipos de propiedades
    if (filters.tipo) {
      filtered = filtered.filter(property => property.tipo === filters.tipo);
    }
    if (filters.precio_min) {
      filtered = filtered.filter(property => 
        property.precio_soles >= parseFloat(filters.precio_min)
      );
    }
    if (filters.precio_max) {
      filtered = filtered.filter(property => 
        property.precio_soles <= parseFloat(filters.precio_max)
      );
    }
    if (filters.habitaciones) {
      filtered = filtered.filter(property => 
        property.habitaciones >= parseInt(filters.habitaciones)
      );
    }
    if (filters.banos) {
      filtered = filtered.filter(property => 
        property.banos >= parseInt(filters.banos)
      );
    }
    if (filters.operacion) {
      filtered = filtered.filter(property => 
        property.operacion === filters.operacion ||
        (property.tipo_operacion && property.tipo_operacion === filters.operacion)
      );
    }

    // Aplicar ordenamiento
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.fecha_publicacion) - new Date(a.fecha_publicacion));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.fecha_publicacion) - new Date(b.fecha_publicacion));
        break;
      case 'price_asc':
        filtered.sort((a, b) => a.precio_soles - b.precio_soles);
        break;
      case 'price_desc':
        filtered.sort((a, b) => b.precio_soles - a.precio_soles);
        break;
      default:
        break;
    }

    setFilteredProperties(filtered);
    setCurrentPage(1);
  }, [properties, filters, searchTerm, sortBy]);

  // Obtener propiedades actuales para la página
  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = filteredProperties.slice(indexOfFirstProperty, indexOfLastProperty);
  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);

  // Manejadores de eventos
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      tipo: '',
      habitaciones: '',
      banos: '',
      precio_min: '',
      precio_max: '',
      operacion: ''
    });
    setSearchTerm('');
  };

  // Renderizar estados de carga y error
  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  }

  // Determinar el título adecuado
  const getTitle = () => {
    if (isOwnProperties) {
      return 'Mis Propiedades';
    } else if (userData) {
      return `Propiedades de ${userData.nombre || ''}`;
    } else if (profileType === 'propiedades_usuario') {
      return 'Propiedades de Usuario';
    } else if (profileType === 'alquileres_usuario') {
      return 'Alquileres de Usuario';
    } else {
      return 'Propiedades';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden z-0">
        {/* Animated Orbs */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gradient-to-br from-blue-400/10 to-indigo-400/10 blur-3xl"
            style={{
              width: `${Math.random() * 400 + 200}px`,
              height: `${Math.random() * 400 + 200}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              zIndex: 0
            }}
            animate={{
              x: [0, Math.random() * 100 - 50, 0],
              y: [0, Math.random() * 100 - 50, 0],
              scale: [1, Math.random() * 0.3 + 1, 1],
            }}
            transition={{
              duration: Math.random() * 10 + 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 relative z-10">
        {/* Header Section */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <SectionTitle 
            title={getTitle()} 
            subtitle={`${filteredProperties.length} propiedades encontradas`} 
          />
        </motion.div>

        {/* Filters Section */}
        <motion.div 
          className="bg-white rounded-xl shadow-xl p-6 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.5,
            delay: 0.2
          }}
        >
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center mb-4">
            {/* Search Bar */}
            <div className="flex-1 relative group">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 
                               group-hover:text-indigo-500 transition-colors" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por título o descripción..."
                className="w-full pl-10 pr-4 py-3 border-2 border-indigo-100 rounded-xl 
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                         transition-all duration-300 group-hover:border-indigo-200"
              />
            </div>

            {/* Filter Toggle */}
            <motion.button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-3 border-2 border-indigo-100 rounded-xl 
                       hover:border-indigo-200 hover:bg-indigo-50 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SlidersHorizontal className="w-5 h-5 text-indigo-500" />
              <span className="font-medium">Filtros</span>
              <ChevronDown 
                className={`w-4 h-4 text-indigo-500 transform transition-transform duration-300 ${
                  showFilters ? 'rotate-180' : ''
                }`} 
              />
            </motion.button>

            {/* Sort Dropdown */}
            <div className="relative">
              <motion.button
                onClick={() => setShowSortOptions(!showSortOptions)}
                className="flex items-center gap-2 px-4 py-3 border-2 border-indigo-100 rounded-xl 
                         hover:border-indigo-200 hover:bg-indigo-50 transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ArrowUpDown className="w-5 h-5 text-indigo-500" />
                <span className="font-medium">Ordenar</span>
                <ChevronDown 
                  className={`w-4 h-4 text-indigo-500 transform transition-transform duration-300 ${
                    showSortOptions ? 'rotate-180' : ''
                  }`} 
                />
              </motion.button>

              <AnimatePresence>
                {showSortOptions && (
                  <motion.div 
                    className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl z-10 overflow-hidden"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {[
                      { value: 'newest', label: 'Más recientes' },
                      { value: 'oldest', label: 'Más antiguos' },
                      { value: 'price_asc', label: 'Menor precio' },
                      { value: 'price_desc', label: 'Mayor precio' }
                    ].map((option) => (
                      <motion.button
                        key={option.value}
                        onClick={() => {
                          setSortBy(option.value);
                          setShowSortOptions(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-indigo-50 flex items-center gap-2
                                 transition-colors duration-200"
                        whileHover={{ backgroundColor: "rgb(238, 242, 255)" }}
                      >
                        {sortBy === option.value && (
                          <Check className="w-4 h-4 text-indigo-500" />
                        )}
                        <span className={sortBy === option.value ? 'font-medium text-indigo-700' : 'text-gray-700'}>
                          {option.label}
                        </span>
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Extended Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div 
                className="mt-6 pt-6 border-t border-indigo-100"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="relative group">
                    <select
                      value={filters.tipo}
                      onChange={(e) => handleFilterChange('tipo', e.target.value)}
                      className="w-full p-3 border-2 border-indigo-100 rounded-xl appearance-none
                               focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                               transition-all duration-300 group-hover:border-indigo-200"
                    >
                      <option value="">Tipo de propiedad</option>
                      <option value="casa">Casa</option>
                      <option value="departamento">Departamento</option>
                      <option value="terreno">Terreno</option>
                      <option value="local">Local Comercial</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-400
                                        group-hover:text-indigo-500 pointer-events-none" />
                  </div>

                  <div className="relative group">
                    <select
                      value={filters.operacion}
                      onChange={(e) => handleFilterChange('operacion', e.target.value)}
                      className="w-full p-3 border-2 border-indigo-100 rounded-xl appearance-none
                               focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                               transition-all duration-300 group-hover:border-indigo-200"
                    >
                      <option value="">Tipo de operación</option>
                      <option value="venta">Venta</option>
                      <option value="alquiler">Alquiler</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-400
                                        group-hover:text-indigo-500 pointer-events-none" />
                  </div>

                  <div className="flex gap-2">
                    <div className="w-1/2 relative group">
                      <input
                        type="number"
                        value={filters.precio_min}
                        onChange={(e) => handleFilterChange('precio_min', e.target.value)}
                        placeholder="Precio mínimo"
                        className="w-full p-3 border-2 border-indigo-100 rounded-xl
                                 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                                 transition-all duration-300 group-hover:border-indigo-200"
                      />
                    </div>
                    <div className="w-1/2 relative group">
                      <input
                        type="number"
                        value={filters.precio_max}
                        onChange={(e) => handleFilterChange('precio_max', e.target.value)}
                        placeholder="Precio máximo"
                        className="w-full p-3 border-2 border-indigo-100 rounded-xl
                                 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                                 transition-all duration-300 group-hover:border-indigo-200"
                      />
                    </div>
                  </div>

                  <div className="relative group">
                    <select
                      value={filters.habitaciones}
                      onChange={(e) => handleFilterChange('habitaciones', e.target.value)}
                      className="w-full p-3 border-2 border-indigo-100 rounded-xl appearance-none
                               focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                               transition-all duration-300 group-hover:border-indigo-200"
                    >
                      <option value="">Habitaciones mínimas</option>
                      <option value="1">1+</option>
                      <option value="2">2+</option>
                      <option value="3">3+</option>
                      <option value="4">4+</option>
                      <option value="5">5+</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-400
                                        group-hover:text-indigo-500 pointer-events-none" />
                  </div>

                  <div className="relative group">
                    <select
                      value={filters.banos}
                      onChange={(e) => handleFilterChange('banos', e.target.value)}
                      className="w-full p-3 border-2 border-indigo-100 rounded-xl appearance-none
                               focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                               transition-all duration-300 group-hover:border-indigo-200"
                    >
                      <option value="">Baños mínimos</option>
                      <option value="1">1+</option>
                      <option value="2">2+</option>
                      <option value="3">3+</option>
                      <option value="4">4+</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-400
                                        group-hover:text-indigo-500 pointer-events-none" />
                  </div>

                  <motion.button
                    onClick={clearFilters}
                    className="flex items-center justify-center gap-2 px-4 py-3 text-red-500 
                             border-2 border-transparent hover:border-red-100 hover:bg-red-50 rounded-xl
                             transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <X className="w-5 h-5" />
                    <span className="font-medium">Limpiar filtros</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Properties Grid */}
{currentProperties.length > 0 ? (
  <motion.div
    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
    variants={staggerContainer}
    initial="hidden"
    animate="visible"
  >
    {currentProperties.map((property) => (
      <motion.div
        key={property.id}
        variants={cardAnimation}
        whileHover="hover"
      >
        <PropertyCard
          property={property}
        />
      </motion.div>
    ))}
  </motion.div>
) : (
  <motion.div 
    className="text-center py-16"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.6 }}
  >
    <div className="bg-white rounded-2xl shadow-lg p-10 max-w-lg mx-auto">
      <AlertCircle className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
      <h3 className="text-2xl font-semibold text-gray-800 mb-2">
        No se encontraron propiedades
      </h3>
      <p className="text-gray-600 mb-6">
        No hay propiedades que coincidan con los filtros seleccionados
      </p>
      {Object.values(filters).some(v => v !== '') && (
        <motion.button
          onClick={clearFilters}
          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl
                  hover:from-blue-600 hover:to-indigo-700 transition-all duration-300"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          Limpiar filtros
        </motion.button>
      )}
    </div>
  </motion.div>
)}

{/* Pagination */}
{filteredProperties.length > propertiesPerPage && (
  <motion.div 
    className="flex justify-center gap-2 mt-8"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 0.3 }}
  >
    <motion.button
      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
      disabled={currentPage === 1}
      className={`px-5 py-3 rounded-xl font-medium flex items-center gap-1 ${
        currentPage === 1
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-white text-indigo-600 hover:bg-indigo-50 shadow-md hover:shadow-lg transition-all duration-300'
      }`}
      whileHover={currentPage !== 1 ? { scale: 1.05 } : {}}
      whileTap={currentPage !== 1 ? { scale: 0.95 } : {}}
    >
      <ChevronDown className="w-5 h-5 transform rotate-90" />
      <span>Anterior</span>
    </motion.button>
    
    <div className="flex gap-2">
      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
        // Mostrar una versión simplificada si hay muchas páginas
        let pageToShow;
        if (totalPages <= 5) {
          pageToShow = i + 1;
        } else {
          if (currentPage <= 3) {
            pageToShow = i + 1;
          } else if (currentPage >= totalPages - 2) {
            pageToShow = totalPages - 4 + i;
          } else {
            pageToShow = currentPage - 2 + i;
          }
        }
        
        return (
          <motion.button
            key={i}
            onClick={() => setCurrentPage(pageToShow)}
            className={`min-w-[3rem] px-4 py-3 rounded-xl font-medium ${
              currentPage === pageToShow
                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg'
                : 'bg-white text-indigo-600 hover:bg-indigo-50 shadow-md hover:shadow-lg transition-all duration-300'
            }`}
            whileHover={currentPage !== pageToShow ? { scale: 1.05 } : {}}
            whileTap={currentPage !== pageToShow ? { scale: 0.95 } : {}}
          >
            {pageToShow}
          </motion.button>
        );
      })}
    </div>
    
    <motion.button
      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
      disabled={currentPage === totalPages}
      className={`px-5 py-3 rounded-xl font-medium flex items-center gap-1 ${
        currentPage === totalPages
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-white text-indigo-600 hover:bg-indigo-50 shadow-md hover:shadow-lg transition-all duration-300'
      }`}
      whileHover={currentPage !== totalPages ? { scale: 1.05 } : {}}
      whileTap={currentPage !== totalPages ? { scale: 0.95 } : {}}
    >
      <span>Siguiente</span>
      <ChevronDown className="w-5 h-5 transform -rotate-90" />
    </motion.button>
  </motion.div>
)}
</div>
</div>
);
};

export default AllProperties;